// 2026-09-24 - FormuLearning - Step 6 입력 공통 부품(숫자 칸·군 머리) (이슈 #63)
// 파일 위치: frontend/src/features/step6/fields.tsx
// 2026-11-03 수정 - 이슈 #114: viewer 는 NumField·되돌리기·다시 불러오기 비활성, Seg 에 `disabled`(입력 선택용).
// 숫자 칸은 타이핑 중엔 로컬 문자열만 바꾸고 blur·Enter 에서 확정한다(Autosave ON 이면 확정마다 PUT).
// 연동값과 다르면 노란 테두리(아티팩트 «수정한 칸은 노란색»), 군 머리는 출처·수정 개수·되돌리기·stale 을 보인다.

import { useState } from 'react'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

export const inputCls = 'w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none'

function fmt(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return ''
  const a = Math.abs(v)
  return a !== 0 && (a < 1e-3 || a >= 1e6) ? v.toExponential() : String(+v.toPrecision(6))
}

interface NumFieldProps {
  label?: string
  unit?: string
  value: number | null | undefined
  onCommit: (v: number | null) => void
  edited?: boolean
  mono?: boolean
  disabled?: boolean
  title?: string
  align?: 'left' | 'center'
}

export function NumField({ label, unit, value, onCommit, edited, mono, disabled, title, align = 'left' }: NumFieldProps) {
  // 편집 중에만 초안 문자열을 둔다(draft=null 이면 value 를 그대로 보인다 — 외부 갱신이 곧바로 반영된다).
  const [draft, setDraft] = useState<string | null>(null)
  const canWrite = useCanWrite()
  const commit = () => {
    if (draft === null) return
    const t = draft.trim()
    setDraft(null)
    const v = t === '' ? null : Number(t)
    if (v !== null && !Number.isFinite(v)) return
    if (v !== (value ?? null)) onCommit(v)
  }
  const input = (
    <input
      type="text"
      inputMode="decimal"
      value={draft ?? fmt(value)}
      disabled={disabled || !canWrite}
      title={canWrite ? title : READ_ONLY_TITLE}
      aria-label={label}
      onFocus={() => setDraft(fmt(value))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
      className={`${inputCls} ${mono ? 'font-mono' : ''} ${align === 'center' ? 'text-center' : ''} disabled:opacity-50`}
      style={edited ? { boxShadow: 'inset 0 0 0 1px var(--color-warn)', backgroundColor: 'rgba(255,184,77,0.10)' } : undefined}
    />
  )
  if (!label) return input
  return (
    <label className="block">
      <span className="mb-0.5 block text-[10px] text-text-secondary">
        {label}{unit ? <span className="text-text-muted"> ({unit})</span> : null}
      </span>
      {input}
    </label>
  )
}

interface GroupHeadProps {
  title: string
  sources: string[]
  editedCount: number
  onRevert: () => void
  stale: boolean
  onReload: () => void
  extra?: React.ReactNode
}

export function GroupHead({ title, sources, editedCount, onRevert, stale, onReload, extra }: GroupHeadProps) {
  const [open, setOpen] = useState(false)
  const canWrite = useCanWrite()
  return (
    <div className="mb-2">
      <div className="flex flex-wrap items-center gap-2">
        <h5 className="text-[11px] font-semibold text-text-primary">{title}</h5>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="rounded-full px-2 py-0.5 text-[10px]"
          style={{ backgroundColor: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }}
          title="입력값 출처"
        >
          ⛓ 출처 {open ? '▲' : '▼'}
        </button>
        {editedCount > 0 && (
          <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--color-warn)' }}>
            연동값에서 {editedCount}개 수정됨
            <button type="button" onClick={onRevert} disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE} className="underline disabled:cursor-not-allowed disabled:opacity-50">되돌리기</button>
          </span>
        )}
        <span className="ml-auto">{extra}</span>
      </div>
      {open && (
        <ul className="mt-1 space-y-0.5 rounded border px-2 py-1 text-[10px] text-text-secondary" style={{ borderColor: 'var(--border-subtle)' }}>
          {sources.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
      {stale && (
        <p className="mt-1 flex items-center gap-2 rounded px-2 py-1 text-[10px]" style={{ color: 'var(--color-warn)', border: '1px solid var(--color-warn)' }}>
          ⚠ 상위 Step 값이 저장 이후 바뀌었습니다.
          <button type="button" onClick={onReload} disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE} className="underline disabled:cursor-not-allowed disabled:opacity-50">다시 불러오기</button>
        </p>
      )}
    </div>
  )
}

// `disabled` — 입력값 선택(저장)일 때 viewer 잠금용. 보기 전환(결과 Linear/Log)은 넘기지 않는다(#114).
export function Seg<T extends string>({ value, options, onChange, disabled }: { value: T; options: { v: T; label: string }[]; onChange: (v: T) => void; disabled?: boolean }) {
  return (
    <div className="inline-flex overflow-hidden rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          aria-pressed={value === o.v}
          onClick={() => onChange(o.v)}
          disabled={disabled}
          title={disabled ? READ_ONLY_TITLE : undefined}
          className="px-2 py-0.5 text-[10px] disabled:cursor-not-allowed disabled:opacity-60"
          style={value === o.v
            ? { backgroundColor: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }
            : { color: 'var(--color-text-secondary)' }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
